import type { ComponentType } from 'react'

import type { IconName } from '../ui/Icon'
import { Assistants } from '../views/Assistants'
import { Cards } from '../views/Cards'
import { Connections } from '../views/Connections'
import { Desk } from '../views/Desk'
import { Exams } from '../views/Exams'
import { Library } from '../views/Library'
import { Notes } from '../views/Notes'
import { Search } from '../views/Search'
import { Subjects } from '../views/Subjects'
import { Tasks } from '../views/Tasks'
import { Timetable } from '../views/Timetable'

/**
 * The areas of MyLib, and the one list every part of the frame reads them from.
 *
 * The navigation, the address, the title in the bar and the document title all come from here, so
 * an area is named once and called the same thing everywhere. Adding one is a line in this list.
 */
export type AreaShape =
  /** Reading matter: it scrolls, and the lane rule applies to it. */
  | 'flow'
  /**
   * A surface: it does not scroll with the text, it fills the pane it is given, and its controls
   * float above it. The frame treats this as no different in rank from a list.
   */
  | 'canvas'

export interface Area {
  readonly id: string
  /** The one word this area is called, in the navigation and in the bar alike. */
  readonly label: string
  readonly icon: IconName
  readonly shape: AreaShape
  readonly View: ComponentType
  /**
   * Whether this area stands in the navigation, or is only ever reached by an address.
   *
   * The search is the first of the second kind, and the reason is what it is: it is not a place you go to
   * before you can look for something — it is over everything, on ⌘K and on the magnifier in the bar. An
   * entry in the navigation would say the opposite, and would be the one entry nobody ever needed.
   */
  readonly listed?: boolean
}

export const AREAS: readonly Area[] = [
  { id: 'desk', label: 'Desk', icon: 'desk', shape: 'canvas', View: Desk },
  { id: 'library', label: 'Library', icon: 'library', shape: 'flow', View: Library },
  { id: 'notes', label: 'Notes', icon: 'notes', shape: 'flow', View: Notes },
  { id: 'tasks', label: 'Tasks', icon: 'tasks', shape: 'flow', View: Tasks },
  /*
    The eighth symbol, and it is one on purpose. The comment below says why the external systems get
    none: setting one up is a one-off. A subject is the opposite — "I open the subject and everything
    is together" is the movement before an exam, and therefore one of the most frequent ways into
    one's own material. The bar carries it: on a phone it scrolls sideways, on iPad and desktop it is
    a column with no ceiling.
  */
  { id: 'subjects', label: 'Subjects', icon: 'subject', shape: 'flow', View: Subjects },
  { id: 'search', label: 'Search', icon: 'search', shape: 'flow', View: Search, listed: false },
  /*
    The second of the unlisted kind, and for the same sort of reason. Setting up a way into a foreign
    system is something somebody does once a semester and then forgets about; it belongs beside who
    you are and which space you are in, which is where the way to it is — the account panel in the
    bar. An eighth symbol in the navigation would give a one-off the same standing as the library.

    The icon is carried because every area has one and never drawn, exactly as the search's is: a
    picture for something the navigation does not show is a picture nothing asks for.
  */
  {
    id: 'connections',
    label: 'External systems',
    icon: 'person',
    shape: 'flow',
    View: Connections,
    listed: false,
  },
  /*
    The third of the unlisted kind, and it earns its place beside the external systems rather than in
    the navigation for the same reason and a second one. Connecting an assistant is a one-off, like
    setting up ILIAS — and it is the *other direction* of the same subject: there MyLib is given a way
    into somebody else's system, here somebody else's system is given a way into MyLib. Both belong
    behind the chip that says who you are and which space you are in.

    It also carries the page where somebody agrees to a program that asked for itself, at
    `#/assistants/allow/<request>`. An address inside the application rather than a screen of the
    service's own, so that whoever arrives there without a session meets the ordinary sign-in and
    comes back to the same place — which the frame already does for every address here.
  */
  {
    id: 'assistants',
    label: 'Assistants',
    icon: 'person',
    shape: 'flow',
    View: Assistants,
    listed: false,
  },
  /*
    The timetable, and the learning diary with it.

    There is deliberately **no diary area**. A diary kept beside the calendar would ask for the day,
    the session and the subject a second time, which is exactly the double bookkeeping MyLib exists
    to remove: the write-up belongs to the appointment, the day of the plan read back *is* the diary,
    and the review before an exam is the subject. An eleventh entry here would be a second way to the
    same place under another name.
  */
  { id: 'timetable', label: 'Timetable', icon: 'timetable', shape: 'flow', View: Timetable },
  { id: 'cards', label: 'Cards', icon: 'cards', shape: 'flow', View: Cards },
  /*
    The tests, beside the cards and deliberately not among them. A card asks what is answered in a
    sentence and marks itself; a test asks for an essay and is marked by somebody reading it. Folding
    the two together would make the second look like a long card, which is the one thing it is not.
  */
  { id: 'exams', label: 'Tests', icon: 'check', shape: 'flow', View: Exams },
]

/** The areas the navigation offers — everything but the ones an address alone opens. */
export const LISTED_AREAS: readonly Area[] = AREAS.filter((area) => area.listed !== false)

export const AREA_IDS: readonly string[] = AREAS.map((area) => area.id)

/**
 * Where signing in leads.
 *
 * The desk, now that there is something on it. It is the surface somebody arranges themselves and
 * the one place that says what belongs with what, which is exactly what "sitting down at your desk
 * in the morning" means — so it is what you arrive at.
 *
 * It was the library until the desk existed, and moving it was this one word. That was the whole
 * claim `AREAS` was built on.
 */
export const ENTRY_AREA_ID = 'desk'

export function areaById(id: string): Area {
  return AREAS.find((area) => area.id === id) ?? entryArea()
}

function entryArea(): Area {
  const area = AREAS.find((candidate) => candidate.id === ENTRY_AREA_ID)
  if (area === undefined) {
    throw new Error(`ENTRY_AREA_ID "${ENTRY_AREA_ID}" is not one of the areas.`)
  }
  return area
}
